import React from 'react';
import { supabase } from '../../../services/supabase';

interface ShowImageProps {
  showSlug: string;
  /** Image filename only (e.g., "123.png") - path is constructed from {storageFolder}/{showSlug}/{imageFile} */
  imageFile?: string | null;
  size: number;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  /** Resize mode: 'fit' preserves aspect ratio (default), 'fill' crops to exact square */
  mode?: 'fit' | 'fill';
  /**
   * Load image directly from storage, bypassing image optimization endpoint.
   * Useful for debugging to ensure fresh images are always loaded without caching issues.
   */
  useDirectStorage?: boolean;
  /** Storage folder - defaults to 'shows', but can be 'stories' for story images */
  storageFolder?: 'shows' | 'stories';
}

/**
 * ShowImage component that displays show/story images using the API service or direct storage access.
 *
 * By default, uses API service endpoints for optimized images:
 * - Shows: /shows/{slug}/image?size={size}
 * - Stories: /stories/{slug}/image?size={size}
 *
 * When useDirectStorage is true, bypasses optimization and loads directly from Supabase Storage
 * with a cache-busting timestamp. This ensures the freshest image is always displayed.
 *
 * The imageFile prop should be just the filename (e.g., "123.png").
 * The full path is constructed as {storageFolder}/{showSlug}/{imageFile}.
 */
export const ShowImage: React.FC<ShowImageProps> = ({
  showSlug,
  imageFile: imageFileProp,
  size,
  alt,
  className,
  style,
  mode,
  useDirectStorage = false,
  storageFolder = 'shows',
}) => {
  // If no image file is provided, return a placeholder
  if (!imageFileProp) {
    return (
      <div
        className={className}
        style={{
          width: size,
          height: size,
          backgroundColor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          ...style,
        }}
      >
        No Image
      </div>
    );
  }

  // Construct full storage path from folder + slug + filename
  // Supports both new format (filename only) and legacy format (full path)
  let storagePath: string;
  if (imageFileProp.includes('/')) {
    // Legacy format: full path already provided (e.g., "shows/my-show/123.png")
    storagePath = imageFileProp;
  } else {
    // New format: construct path from storageFolder, showSlug, and filename
    storagePath = `${storageFolder}/${showSlug}/${imageFileProp}`;
  }

  let imageUrl: string;

  if (useDirectStorage) {
    // Load directly from Supabase Storage, bypassing optimization
    // This ensures fresh images are always loaded without caching issues
    const { data } = supabase.storage.from('media').getPublicUrl(storagePath);

    // Add timestamp for cache busting
    const timestamp = Date.now();
    imageUrl = `${data.publicUrl}?t=${timestamp}`;
  } else {
    // Use API service endpoint (default behavior)
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

    if (!apiBaseUrl) {
      // Fallback to direct storage if API not configured
      const { data } = supabase.storage.from('media').getPublicUrl(storagePath);
      imageUrl = data.publicUrl;
    } else {
      // Map storage folder names to public API endpoint paths
      // Storage: shows/stories → API endpoints: shows/drops (Issue #6882b18b)
      const apiEndpoint = storageFolder === 'shows' ? 'shows' : 'drops';

      // Construct the API endpoint URL with cache-busting timestamp
      // Format: /v1/channels/{slug}/image?size=70&mode=fill&t={timestamp}
      const modeParam = mode ? `&mode=${mode}` : '';
      const timestamp = Date.now();
      imageUrl = `${apiBaseUrl}/${apiEndpoint}/${showSlug}/image?size=${size}${modeParam}&t=${timestamp}`;
    }
  }

  return (
    <img
      src={imageUrl}
      alt={alt || `${showSlug} show image`}
      className={className}
      style={{
        width: size,
        height: size,
        objectFit: 'cover',
        borderRadius: '4px',
        ...style,
      }}
      onError={e => {
        // If image fails to load, replace with placeholder
        const target = e.target as HTMLImageElement;

        // Only create placeholder if it hasn't been created yet
        // (prevents multiple placeholders from being created on retry attempts)
        if (!target.dataset.errorHandled) {
          target.dataset.errorHandled = 'true';

          // Create a placeholder div
          const placeholder = document.createElement('div');
          placeholder.style.width = `${size}px`;
          placeholder.style.height = `${size}px`;
          placeholder.style.backgroundColor = '#f5f5f5';
          placeholder.style.display = 'flex';
          placeholder.style.alignItems = 'center';
          placeholder.style.justifyContent = 'center';
          placeholder.style.color = '#999';
          placeholder.style.border = '1px solid #e0e0e0';
          placeholder.style.borderRadius = '4px';
          placeholder.textContent = 'Image Error';

          // Replace the img element with the placeholder
          target.parentNode?.replaceChild(placeholder, target);
        }
      }}
    />
  );
};
